// =============================================================================
// TurnkeyKeyManager — Standalone Turnkey private key for encryption KDF
//
// Creates and manages an invisible SECP256K1 private key in the user's Turnkey
// sub-org. This key is NOT a wallet — it has no accounts, no addresses shown to
// the user, and no custodial implications. It exists solely to provide
// deterministic server-side signing for AES key derivation (SecureStorage).
//
// The private key ID is stored in localStorage keyed by userId. If cleared,
// it can be re-discovered via fetchPrivateKeys / httpClient.getPrivateKeys.
// =============================================================================

import { notEmpty } from "@orb-labs/types";
import type { TurnkeySDKClientBase } from "@turnkey/react-wallet-kit";

const STORAGE_KEY_NAME = "bullrun-storage-key";

function localStorageKey(userId: string): string {
  return `bullrun_turnkey_storage_key_${userId}`;
}

/**
 * Ensure the user has a standalone Turnkey private key for encryption.
 * Creates one if absent, or re-discovers it if localStorage was cleared.
 *
 * Returns the `privateKeyId` for use with `signRawPayload`.
 */
export async function ensureStorageKey(httpClient: TurnkeySDKClientBase, userId: string): Promise<string> {
  const cached = getStorageKeyId(userId);
  if (notEmpty(cached)) return cached;

  const existing = await rediscoverStorageKey(httpClient, userId);
  if (notEmpty(existing)) return existing;

  return createStorageKey(httpClient, userId);
}

/**
 * Read the cached privateKeyId from localStorage.
 */
export function getStorageKeyId(userId: string): string | undefined {
  try {
    const value = localStorage.getItem(localStorageKey(userId));
    return notEmpty(value) ? value : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Clear the cached privateKeyId from localStorage (e.g. on logout).
 * The key itself persists in Turnkey for next login.
 */
export function clearStorageKeyId(userId: string): void {
  try {
    localStorage.removeItem(localStorageKey(userId));
  } catch {
    // Best-effort
  }
}

/**
 * Sign a purpose string with the standalone private key.
 * Used by SecureStorage to derive AES encryption keys.
 */
export async function signForKeyDerivation(
  httpClient: TurnkeySDKClientBase,
  privateKeyId: string,
  purpose: string
): Promise<{ r: string; s: string }> {
  const result = await httpClient.signRawPayload({
    signWith: privateKeyId,
    payload: purpose,
    encoding: "PAYLOAD_ENCODING_TEXT_UTF8",
    hashFunction: "HASH_FUNCTION_SHA256",
  });
  return { r: result.r, s: result.s };
}

// ---------------------------------------------------------------------------
// Internal
// ---------------------------------------------------------------------------

async function rediscoverStorageKey(httpClient: TurnkeySDKClientBase, userId: string): Promise<string | undefined> {
  try {
    const response = await httpClient.getPrivateKeys();
    const match = response.privateKeys.find((pk) => pk.privateKeyName === STORAGE_KEY_NAME);
    if (notEmpty(match)) {
      localStorage.setItem(localStorageKey(userId), match.privateKeyId);
      return match.privateKeyId;
    }
  } catch {
    // First-time user or network error — fall through to creation
  }
  return undefined;
}

async function createStorageKey(httpClient: TurnkeySDKClientBase, userId: string): Promise<string> {
  const response = await httpClient.createPrivateKeys({
    privateKeys: [
      {
        privateKeyName: STORAGE_KEY_NAME,
        curve: "CURVE_SECP256K1",
        privateKeyTags: [],
        addressFormats: [],
      },
    ],
  });

  const created = response.privateKeys[0];
  if (!notEmpty(created?.privateKeyId)) {
    throw new Error("[TurnkeyKeyManager] createPrivateKeys returned no privateKeyId");
  }
  localStorage.setItem(localStorageKey(userId), created.privateKeyId);
  return created.privateKeyId;
}
